import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Receipt, CalendarClock, Hourglass, Info, Coins, ChevronLeft, ChevronRight } from "lucide-react";

// ---- Formatage FR --------------------------------------------------------
function eur(n) {
  const v = Number(n);
  if (!isFinite(v)) return "—";
  return v.toLocaleString("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });
}
function frDate(iso) {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return s;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

// Affichage NÉGATIF des déductions (pré-attritions, refus) : « −1 », ou « 0 » si nul.
function negDisplay(v) {
  const n = Number(v) || 0;
  return n > 0 ? `−${n}` : "0";
}

// Une ligne de « primes / bonus & autres frais ».
// • Ligne SIMPLE (detail === null) : affichage en ligne, inchangé.
// • Ligne CALCULÉE (auto === true && detail = tableau non vide) : accordéon dépliant qui
//   détaille, recruteur par recruteur, COMMENT le montant est obtenu (ex. commission de
//   coordination) — mêmes colonnes que la plateforme reporting Shariti.
function BonusRow({ b, catLabel, testid }) {
  const [open, setOpen] = useState(false);
  const detail = Array.isArray(b?.detail) ? b.detail : null;
  // Détection ROBUSTE (conseil reporting) : on déplie dès qu'il y a une ventilation `detail`
  // non vide — sans dépendre du libellé, et même si `auto` n'est pas fourni. `auto === true`
  // reste accepté comme indice complémentaire.
  const isAccordion = (b?.auto === true || !!detail) && !!detail && detail.length > 0;
  const amountStr = Number(b?.montant) < 0 ? eur(b?.montant) : `+${eur(b?.montant)}`;

  // --- Ligne simple (comportement d'origine) ---
  if (!isAccordion) {
    return (
      <div className="flex items-start justify-between gap-2 px-3 py-1.5 text-[12px]" data-testid={testid}>
        <div className="flex min-w-0 flex-1 items-start gap-1.5">
          <span className="mt-0.5 shrink-0 rounded-full bg-[rgba(238,114,46,0.12)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[var(--shariti-orange)]">{catLabel}</span>
          <div className="min-w-0">
            <div className="break-words font-semibold text-ink">{b.label}</div>
            {b.quantite != null && b.montant_unitaire != null && (
              <div className="mt-0.5 text-[11px] text-muted-ink">{b.quantite} × {eur(b.montant_unitaire)}</div>
            )}
          </div>
        </div>
        <span className="shrink-0 pt-0.5 font-bold text-ink">{amountStr}</span>
      </div>
    );
  }

  // --- Ligne CALCULÉE : accordéon (ex. commission de coordination) ---
  const nb = detail.length;
  const totalDetail = detail.reduce((a, d) => a + (Number(d.montant) || 0), 0);
  return (
    <div data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-start justify-between gap-2 px-3 py-1.5 text-left text-[12px] transition-colors hover:bg-[var(--surface-2)]"
        data-testid={`${testid}-toggle`}
      >
        <div className="flex min-w-0 flex-1 items-start gap-1.5">
          <ChevronRight className={`mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-ink transition-transform ${open ? "rotate-90" : ""}`} />
          <div className="min-w-0">
            <div className="break-words font-semibold text-ink">{b.label}</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-muted-ink">
              <span className="rounded bg-[rgba(238,114,46,0.12)] px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[var(--shariti-orange)]">calculée</span>
              <span>· {nb} recruteur{nb > 1 ? "s" : ""} rattaché{nb > 1 ? "s" : ""}</span>
            </div>
          </div>
        </div>
        <span className="shrink-0 pt-0.5 font-bold text-ink">{amountStr}</span>
      </button>

      {open && (
        <div className="px-3 pb-2" data-testid={`${testid}-detail`}>
          <div className="overflow-x-auto rounded-lg border border-[var(--border-soft)]">
            <table className="w-full border-collapse text-[11px]">
              <thead>
                <tr className="bg-[var(--surface-2)] text-left text-muted-ink">
                  <th className="whitespace-nowrap px-2 py-1.5 font-bold">Recruteur</th>
                  <th className="whitespace-nowrap px-2 py-1.5 text-center font-bold">Enreg.</th>
                  <th className="whitespace-nowrap px-2 py-1.5 text-center font-bold">Pré-attr.</th>
                  <th className="whitespace-nowrap px-2 py-1.5 text-center font-bold">Refus</th>
                  <th className="whitespace-nowrap px-2 py-1.5 text-center font-bold">Nets</th>
                  <th className="whitespace-nowrap px-2 py-1.5 text-center font-bold">€/BS</th>
                  <th className="whitespace-nowrap px-2 py-1.5 text-right font-bold">Montant</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-soft)]">
                {detail.map((d, j) => (
                  <tr key={j} data-testid={`${testid}-row-${j}`}>
                    <td className="px-2 py-1.5">
                      <div className="font-semibold text-ink">{d.recruteur || "—"}{d.nom ? ` · ${d.nom}` : ""}</div>
                      {(d.date_debut || d.date_fin) && (
                        <div className="text-[10px] text-muted-ink">({frDate(d.date_debut)} → {d.date_fin ? frDate(d.date_fin) : "…"})</div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-center">{d.enregistres ?? "—"}</td>
                    <td className={`whitespace-nowrap px-2 py-1.5 text-center ${Number(d.preattritions) > 0 ? "font-semibold text-[var(--shariti-orange)]" : "text-muted-ink"}`}>{negDisplay(d.preattritions)}</td>
                    <td className={`whitespace-nowrap px-2 py-1.5 text-center ${Number(d.refus) > 0 ? "font-semibold text-[var(--shariti-orange)]" : "text-muted-ink"}`}>{negDisplay(d.refus)}</td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-center font-bold text-[var(--shariti-orange)]">{d.nets ?? "—"}</td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-center">{d.taux != null ? `${d.taux} €` : "—"}</td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-right font-bold">{eur(d.montant)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[var(--border-soft)] bg-[var(--surface-2)] font-bold text-ink">
                  <td className="px-2 py-1.5" colSpan={6}>Total commission</td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-right text-[var(--shariti-orange)]">{eur(totalDetail)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="mt-1.5 text-[10px] leading-snug text-muted-ink">
            Nets = Enregistrés − Pré-attrition − Refus ; Montant = Nets × €/BS.
          </p>
        </div>
      )}
    </div>
  );
}

// Carte détaillée d'UN relevé mensuel (période, prélèvement, semaines, totaux).
function PaieReleveCard({ r }) {
  const enAttente = Number(r?.nb_semaines_en_attente) > 0;
  const semaines = Array.isArray(r?.semaines) ? r.semaines : [];

  // Décomposition « du brut au net » — affichée UNIQUEMENT si le Reporting l'envoie.
  // On ne montre que les colonnes réellement fournies (jamais de colonne vide).
  const DECOMP = [
    { key: "enregistres", label: "Enreg.", full: "Bulletins enregistrés (bruts)", amber: false },
    { key: "livres", label: "Livrés", full: "Bulletins livrés", amber: false },
    { key: "mandats", label: "Mandats", full: "Mandats signés", amber: false },
    { key: "pre_attrition", label: "Pré-attr.", full: "Pré-attrition (déductions)", amber: true },
    { key: "refus", label: "Refus", full: "Refus", amber: true },
    { key: "retards", label: "Retards", full: "Retards éventuels", amber: true },
  ];
  const cols = DECOMP.filter((c) => semaines.some((s) => s[c.key] != null));
  const hasDecomp = cols.length > 0;
  const totalCols = 5 + cols.length; // Semaine + décompo + (BS nets, Palier, €/BS, Montant)
  const sum = (key) => semaines.reduce((a, s) => a + (Number(s[key]) || 0), 0);
  const numCell = (v, amber) => {
    if (v == null) return <span className="text-muted-ink">—</span>;
    if (amber) return <span className={Number(v) > 0 ? "font-semibold text-[var(--shariti-orange)]" : "text-muted-ink"}>{v}</span>;
    return <span>{v}</span>;
  };

  // Primes / bonus & autres frais (hors bulletins) — ex. « Frais essence ». Affichés SEULEMENT
  // s'il y en a, avec le détail (label · qté × PU · montant) pour une facture claire.
  const bonus = Array.isArray(r?.bonus) ? r.bonus : [];
  const hasBonus = bonus.length > 0 || Number(r?.total_bonus) > 0;
  const bulletinsTotal = r?.total_montant_bulletins != null ? r.total_montant_bulletins : sum("montant");
  // Repli robuste : si le Reporting n'envoie pas explicitement le total des primes/frais
  // ou le grand total, on les recalcule (bulletins + frais) pour rester cohérent.
  const bonusTotal = r?.total_bonus != null ? r.total_bonus : bonus.reduce((a, b) => a + (Number(b.montant) || 0), 0);
  const grandTotal = r?.total_montant != null ? r.total_montant : bulletinsTotal + bonusTotal;
  const bonusCat = (c) => {
    const m = { frais: "Frais", prime: "Prime", bonus: "Bonus", spot: "Spot", rebook: "Spot", regularisation: "Régul." };
    return m[String(c || "").toLowerCase()] || "Ligne";
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]" data-testid={`ma-paie-releve-${r.ym || "x"}`}>
      {/* En-tête : période / association + total à facturer */}
      <div className="flex items-start justify-between gap-3 border-b border-[var(--border-soft)] px-3 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-bold text-muted-ink">{r?.periode?.label || ""}</span>
            {enAttente ? (
              <Badge className="shrink-0 border-0 bg-[rgba(214,138,20,0.14)] px-1.5 py-0 text-[9px] font-bold text-[#9A6207]">provisoire</Badge>
            ) : null}
          </div>
          {r?.association ? <div className="mt-0.5 break-words text-[11px] font-medium text-muted-ink">{r.association}</div> : null}
        </div>
        <span className="shrink-0 text-lg font-extrabold text-[var(--shariti-orange)]" data-testid={`ma-paie-total-${r.ym || "x"}`}>
          {eur(grandTotal)}
        </span>
      </div>

      <div className="px-3 py-3">
        {/* Bandeau prélèvement */}
        <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl bg-[var(--surface-2)] px-3 py-2 text-[11px] font-semibold text-ink">
          <span className="flex items-center gap-1">
            <CalendarClock className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
            Prélèvement le <span className="font-extrabold">{frDate(r.prelevement_date)}</span>
          </span>
        </div>

        {/* Tableau des semaines */}
        <div className="overflow-x-auto rounded-xl border border-[var(--border-soft)]">
          <table className="w-full border-collapse text-left text-[11px]" data-testid={`ma-paie-semaines-${r.ym || "x"}`}>
            <thead>
              <tr className="bg-[var(--surface-2)] text-[10px] uppercase tracking-wide text-muted-ink">
                <th className="whitespace-nowrap px-2 py-1.5 font-bold">Semaine</th>
                {cols.map((c) => (
                  <th key={c.key} className="whitespace-nowrap px-2 py-1.5 text-center font-bold" title={c.full}>{c.label}</th>
                ))}
                <th className="whitespace-nowrap px-2 py-1.5 text-center font-bold">{hasDecomp ? "Net" : "BS nets"}</th>
                <th className="whitespace-nowrap px-2 py-1.5 font-bold">Palier</th>
                <th className="whitespace-nowrap px-2 py-1.5 text-center font-bold">€/BS</th>
                <th className="whitespace-nowrap px-2 py-1.5 text-right font-bold">Montant</th>
              </tr>
            </thead>
            <tbody>
              {semaines.map((s, i) => (
                <tr
                  key={s.monday || i}
                  className={`border-t border-[var(--border-soft)] ${s.en_attente ? "bg-[rgba(214,138,20,0.06)] text-muted-ink" : "text-ink"}`}
                  data-testid={`ma-paie-semaine-${r.ym || "x"}-${i}`}
                >
                  <td className="px-2 py-1.5 font-semibold">
                    <div className="flex items-center gap-1.5">
                      <span className="whitespace-nowrap">{s.label || "—"}</span>
                      {s.en_attente ? (
                        <span className="inline-flex items-center gap-0.5 rounded bg-[rgba(214,138,20,0.14)] px-1 py-0.5 text-[9px] font-bold text-[#9A6207]" data-testid={`ma-paie-attente-${r.ym || "x"}-${i}`}>
                          <Hourglass className="h-2.5 w-2.5" /> en attente
                        </span>
                      ) : null}
                    </div>
                  </td>
                  {cols.map((c) => (
                    <td key={c.key} className="px-2 py-1.5 text-center" data-testid={`ma-paie-${c.key}-${r.ym || "x"}-${i}`}>
                      {numCell(s[c.key], c.amber)}
                    </td>
                  ))}
                  <td className={`whitespace-nowrap px-2 py-1.5 text-center font-bold ${hasDecomp ? "text-[var(--shariti-orange)]" : ""}`}>{s.bs_nets ?? "—"}</td>
                  <td className="whitespace-nowrap px-2 py-1.5 capitalize">{s.palier || "—"}</td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-center">{s.taux_bs != null ? `${s.taux_bs} €` : "—"}</td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-right font-bold">{eur(s.montant)}</td>
                </tr>
              ))}
              {semaines.length === 0 && (
                <tr className="border-t border-[var(--border-soft)]">
                  <td colSpan={totalCols} className="px-2 py-3 text-center text-muted-ink">Aucune semaine.</td>
                </tr>
              )}
            </tbody>
            {hasDecomp && semaines.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-[var(--border-soft)] bg-[var(--surface-2)] font-bold text-ink" data-testid={`ma-paie-totaux-${r.ym || "x"}`}>
                  <td className="px-2 py-1.5 text-[10px] uppercase tracking-wide text-muted-ink">Total</td>
                  {cols.map((c) => (
                    <td key={c.key} className="px-2 py-1.5 text-center">{numCell(sum(c.key), c.amber)}</td>
                  ))}
                  <td className="px-2 py-1.5 text-center text-[var(--shariti-orange)]">{r.total_bs_nets ?? sum("bs_nets")}</td>
                  <td className="px-2 py-1.5" />
                  <td className="px-2 py-1.5" />
                  {/* Montant du TOTAL = uniquement les bulletins (hors primes/frais), pour rester
                      cohérent avec le pied « bulletins + frais = total à facturer ». */}
                  <td className="px-2 py-1.5 text-right text-[var(--shariti-orange)]">{eur(bulletinsTotal)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Note « provisoire » si des semaines sont en attente */}
        {enAttente && (
          <div className="mt-2 flex items-start gap-1.5 rounded-xl border border-[rgba(214,138,20,0.35)] bg-[rgba(214,138,20,0.08)] px-3 py-2 text-[11px] font-medium leading-snug text-[#7A4E06]" data-testid={`ma-paie-note-attente-${r.ym || "x"}`}>
            <Info className="mt-[1px] h-3.5 w-3.5 shrink-0" />
            Montants provisoires, non encore facturables tant qu'ils ne sont pas confirmés.
          </div>
        )}

        {/* Primes / bonus & autres frais (hors bulletins) — ex. Frais essence refacturés */}
        {hasBonus && (
          <div className="mt-2 overflow-hidden rounded-xl border border-[var(--border-soft)]" data-testid={`ma-paie-frais-${r.ym || "x"}`}>
            <div className="flex items-center gap-1.5 border-b border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-ink">
              <Coins className="h-3.5 w-3.5 text-[var(--shariti-orange)]" /> Primes / bonus &amp; autres frais
              <span className="font-medium normal-case text-muted-ink">(hors bulletins)</span>
            </div>
            <div className="divide-y divide-[var(--border-soft)]">
              {bonus.map((b, i) => (
                <BonusRow key={i} b={b} catLabel={bonusCat(b.categorie)} testid={`ma-paie-frais-ligne-${r.ym || "x"}-${i}`} />
              ))}
            </div>
          </div>
        )}

        {/* Pied de carte : détail bulletins + frais = total à facturer */}
        {hasBonus ? (
          <div className="mt-2 space-y-1 rounded-xl bg-[var(--surface-2)] px-3 py-2" data-testid={`ma-paie-footer-${r.ym || "x"}`}>
            <div className="flex items-center justify-between text-[11px] text-muted-ink">
              <span>Sous-total bulletins <span className="font-semibold text-ink">({r.total_bs_nets ?? "—"} BS nets)</span></span>
              <span className="font-semibold text-ink">{eur(bulletinsTotal)}</span>
            </div>
            <div className="flex items-center justify-between text-[11px] text-muted-ink">
              <span>Total primes / frais</span>
              <span className="font-semibold text-[var(--shariti-orange)]">+{eur(bonusTotal)}</span>
            </div>
            <div className="flex items-center justify-between border-t border-[var(--border-soft)] pt-1 text-[11px] font-bold text-ink">
              <span>Total à facturer</span>
              <span className="text-sm font-extrabold text-[var(--shariti-orange)]" data-testid={`ma-paie-footer-total-${r.ym || "x"}`}>{eur(grandTotal)}</span>
            </div>
          </div>
        ) : (
          <div className="mt-2 flex items-center justify-between gap-3 rounded-xl bg-[var(--surface-2)] px-3 py-2">
            <span className="text-[11px] font-semibold text-muted-ink">
              Total BS nets : <span className="font-extrabold text-ink" data-testid={`ma-paie-bs-${r.ym || "x"}`}>{r.total_bs_nets ?? "—"}</span>
            </span>
            <span className="text-[11px] font-bold text-ink">
              Total à facturer{" "}
              <span className="text-sm font-extrabold text-[var(--shariti-orange)]" data-testid={`ma-paie-footer-total-${r.ym || "x"}`}>
                {eur(grandTotal)}
              </span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// Onglet « Ma paie » — relevés de paie mensuels (lecture seule, données Reporting).
// Navigation MOIS PAR MOIS via les flèches ‹ › (un relevé affiché à la fois, du plus
// récent d'abord). N'affiche QUE ce que l'API renvoie ; jamais si non autorisé (géré en amont).
export function MaPaie({ data }) {
  const releves = Array.isArray(data?.releves) ? data.releves : [];
  const [idx, setIdx] = useState(0);

  // Revenir au mois le plus récent si le jeu de données change.
  useEffect(() => { setIdx(0); }, [data]);

  if (!data?.visible || releves.length === 0) {
    return (
      <div className="grid place-items-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-12 text-center" data-testid="ma-paie-empty">
        <Receipt className="h-7 w-7 text-muted-ink" />
        <p className="mt-2 text-sm font-semibold text-ink">Aucun relevé disponible</p>
        <p className="mt-1 max-w-xs text-xs text-muted-ink">
          Vos relevés de paie apparaîtront ici dès qu'ils seront publiés.
        </p>
      </div>
    );
  }

  const safeIdx = Math.min(Math.max(idx, 0), releves.length - 1);
  const r = releves[safeIdx];
  const canOlder = safeIdx < releves.length - 1;  // ‹ mois plus ANCIEN
  const canNewer = safeIdx > 0;                   // › mois plus RÉCENT

  const navBtn = "grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-[var(--border-soft)] text-ink transition-colors enabled:hover:bg-[var(--surface-2)] disabled:cursor-not-allowed disabled:opacity-35";

  return (
    <div className="space-y-3" data-testid="ma-paie-tab">
      <div className="flex items-center gap-1.5 px-1 text-[11px] font-bold uppercase tracking-wide text-muted-ink">
        <Receipt className="h-4 w-4 text-[var(--shariti-orange)]" /> Mes relevés de paie
      </div>

      {/* Navigateur de mois */}
      <div className="flex items-center justify-between gap-2 rounded-2xl border border-[var(--border-soft)] bg-white p-1.5 shadow-[var(--shadow-sm)]">
        <button type="button" className={navBtn} disabled={!canOlder} onClick={() => setIdx(safeIdx + 1)}
          aria-label="Mois précédent" title="Mois précédent" data-testid="ma-paie-prev">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 text-center">
          <div className="truncate text-sm font-extrabold capitalize text-ink" data-testid="ma-paie-current-month">
            {r.mois_label || r.ym || "—"}
          </div>
          {releves.length > 1 && (
            <div className="text-[10px] font-semibold text-muted-ink" data-testid="ma-paie-position">
              {safeIdx + 1} / {releves.length}
            </div>
          )}
        </div>
        <button type="button" className={navBtn} disabled={!canNewer} onClick={() => setIdx(safeIdx - 1)}
          aria-label="Mois suivant" title="Mois suivant" data-testid="ma-paie-next">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Relevé du mois sélectionné */}
      <PaieReleveCard r={r} />
    </div>
  );
}

export default MaPaie;
